import React, { createContext, useContext } from "react";
import { useSelector } from "react-redux";

import { AVAILABLE_PERMISSIONS } from "@/constants/permissions";
import { RootState } from "@/store";

interface PermissionContextType {
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  permissions: string[];
}

const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined,
);

export function PermissionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useSelector((state: RootState) => state.auth.user);

  // Admin users automatically have all permissions
  const permissions =
    user?.role === "admin"
      ? (AVAILABLE_PERMISSIONS as unknown as string[])
      : user?.permissions || [];

  const hasPermission = (permission: string): boolean => {
    // Admin always has all permissions
    if (user?.role === "admin") return true;
    return permissions.includes(permission);
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    // Admin always has all permissions
    if (user?.role === "admin") return true;
    return requiredPermissions.every((perm) => permissions.includes(perm));
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    // Admin always has all permissions
    if (user?.role === "admin") return true;
    return requiredPermissions.some((perm) => permissions.includes(perm));
  };

  return (
    <PermissionContext.Provider
      value={{
        hasAllPermissions,
        hasAnyPermission,
        hasPermission,
        permissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermission must be used within a PermissionProvider");
  }
  return context;
}
